import type { SlotApp } from "../app/createSlotApp";
import type { CoreServices } from "./CoreServices";
import type { OfflineRoundProvider } from "../composition/GameDefinition";
import { DevBadgeView } from "../dev/DevBadgeView";
import type { DevControlPort } from "../dev/DevControlPort";
import { DevTools } from "../dev/DevTools";
import { DEFAULT_DEVTOOLS_CONFIG } from "../dev/DevToolsConfig";
import { PixiDevControlPort } from "../dev/PixiDevControlPort";
import type { DevMenuSectionSource } from "../dev/menu/DevMenuModel";
import type { SpinCoordinator } from "../spin/SpinCoordinator";
import type { Log, PixiPresentationTicker } from "@slot/presentation";

/** What the dev menu builds on once the runtime exists. */
export interface InstalledDevTools {
  badge: DevBadgeView;
  devControl: DevControlPort;
}

/**
 * The dev tools at the start of boot, all behind dev mode like the hook (decision 30): the badge and the
 * KeyP / KeyN hotkeys. The dev menu follows once the runtime exists (`loadDevMenu`); its time section
 * replaced the frame-step overlay `?framestep=1` showed until 2026-10-01 (decision 37).
 */
export function installDevTools(input: { slot: SlotApp; presentationTicker: PixiPresentationTicker; devModeEnabled: boolean; log: Log }): InstalledDevTools | null {
  if (!input.devModeEnabled) return null;
  const devConfig = { ...DEFAULT_DEVTOOLS_CONFIG, enabled: true };
  const badge = new DevBadgeView({ overlayRoot: input.slot.domOverlayRoot, viewport: input.slot.viewport });
  const devControl = new PixiDevControlPort(input.presentationTicker);
  new DevTools({ config: devConfig, badgePanel: badge, devControl, log: input.log }).start();
  input.log(`[DevTools] enabled badge=on key=${devConfig.pauseToggleKeyCode}`);
  return { badge, devControl };
}

/**
 * The dev menu (decision 37), once the runtime exists. Its module is a chunk of its own, fetched only
 * here, so a boot without dev mode never downloads it. The boot waits for it, so the badge's tap and
 * `?fixtures=` are live when the game is ready; a chunk that fails to load is logged and the game runs on
 * without the menu.
 */
export function loadDevMenu(input: {
  devTools: InstalledDevTools;
  slot: SlotApp;
  presentationTicker: PixiPresentationTicker;
  spinCoordinator: SpinCoordinator;
  services: CoreServices;
  /** The offline server; null on a backend. */
  offlineRounds: OfflineRoundProvider | null;
  backendBaseUrl: string;
  gameSections: readonly DevMenuSectionSource[];
  log: Log;
}): Promise<void> {
  const { services } = input;
  return import("../dev/menu/installDevMenu")
    .then(({ installDevMenu }) =>
      installDevMenu({
        overlayRoot: input.slot.domOverlayRoot,
        viewport: input.slot.viewport,
        badge: input.devTools.badge,
        devControl: input.devTools.devControl,
        pauseChanges: (listener) => input.presentationTicker.subscribePaused(() => listener()),
        offlineRounds: input.offlineRounds,
        state: {
          spinCoordinator: input.spinCoordinator,
          balanceStore: services.balanceStore,
          featureStateStore: services.featureStateStore,
          featureModeStore: services.featureModeStore,
          autoSpinStore: services.autoSpinStore,
          interactionBlockStore: services.interactionBlockStore,
          canvasOverlayStore: services.canvasOverlayStore,
          formatter: services.moneyContext.formatter,
          backendBaseUrl: input.backendBaseUrl,
        },
        gameSections: input.gameSections,
        search: window.location.search,
        log: input.log,
      }),
    )
    .then(() => undefined)
    .catch((error: unknown) => input.log(`[DevMenu] not installed: ${error instanceof Error ? error.message : String(error)}`));
}
